import React, { useState, useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { AesRuleDef, AesSeverity } from 'api/AesAnalyzerAPI';
import {
  AES_SEVERITY_LABEL,
  AES_SEVERITY_ORDER,
} from 'components/Analyzers/AesSeverityChip';

/** Filter value the server reads as "this column is empty". */
export const AES_BLANK_OPTION = '__blank__';

export interface AesFilters {
  severities: string[];
  rules: string[];
  countries: string[];
  licenseTypes: string[];
  routed: string[];
}

export const EMPTY_AES_FILTERS: AesFilters = {
  severities: [],
  rules: [],
  countries: [],
  licenseTypes: [],
  routed: [],
};

export const countAesFilters = (f: AesFilters): number =>
  f.severities.length +
  f.rules.length +
  f.countries.length +
  f.licenseTypes.length +
  f.routed.length;

interface IAesFilterModalProps {
  handleClose: () => void;
  active: AesFilters;
  /** Values present on the run; the modal offers only these. */
  available: {
    severities: string[];
    rules: string[];
    countries: string[];
    licenseTypes: string[];
    routed: string[];
  };
  rules: AesRuleDef[];
  onApplyFilters: (filters: AesFilters) => void;
  isLoading: boolean;
}

const columnTitle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 600,
  color: '#667085',
  textTransform: 'uppercase',
  marginBottom: '16px',
  letterSpacing: '0.05em',
};

const columnList: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  maxHeight: '400px',
  overflowY: 'auto',
  paddingRight: '4px',
};

/** One checkbox row, same markup as the ACE/Tariff filter modals. */
const CheckRow: React.FC<{
  selected: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ selected, onToggle, children }) => (
  <div
    className='bill-container-fit-screen-content-row-checkbox'
    onClick={onToggle}
  >
    <div className='bill-container-fit-screen-content-row-input'>
      <div
        className={`bill-container-fit-screen-content-row-input-icon ${
          selected ? 'selected' : ''
        }`}
      >
        <div
          className={`bill-container-fit-screen-content-row-input-icon-rnd ${
            selected ? 'selected' : ''
          }`}
        />
      </div>
    </div>
    <div className='bill-container-fit-screen-content-row-checkbox-text'>
      <p style={{ margin: 0 }}>{children}</p>
    </div>
  </div>
);

/**
 * AES Analyzer filter modal — same shell and checkbox markup as the ACE
 * Analyzer's UnifiedFilterModal, with the AES dimensions: severity, finding,
 * destination country, license type and routed. Multi-select; values within a
 * column are OR'd, columns are AND'd (the server applies them). Findings are
 * listed by flag name; the rule id is only the value sent to the server.
 */
const AesFilterModal: React.FC<IAesFilterModalProps> = ({
  handleClose,
  active,
  available,
  rules,
  onApplyFilters,
  isLoading,
}) => {
  const [local, setLocal] = useState<AesFilters>(active);

  useEffect(() => {
    setLocal(active);
  }, [active]);

  const toggle = (key: keyof AesFilters, value: string) =>
    setLocal((prev) => ({
      ...prev,
      [key]: prev[key].includes(value)
        ? prev[key].filter((v) => v !== value)
        : [...prev[key], value],
    }));

  const severities = AES_SEVERITY_ORDER.filter((s) =>
    available.severities.includes(s),
  );
  // Findings in the analyzer's rule order, only those that fired on this run.
  const ruleOptions = rules.filter((r) => available.rules.includes(r.id));
  const label = (v: string) => (v === AES_BLANK_OPTION ? '(Blank)' : v);

  const columns: Array<{
    key: keyof AesFilters;
    title: string;
    options: Array<{ value: string; text: React.ReactNode }>;
  }> = [
    {
      key: 'severities',
      title: 'Severity',
      options: severities.map((s) => ({
        value: s,
        text: AES_SEVERITY_LABEL[s as AesSeverity],
      })),
    },
    {
      key: 'rules',
      title: 'Findings',
      options: ruleOptions.map((r) => ({ value: r.id, text: r.flag })),
    },
    {
      key: 'countries',
      title: 'Destination Country',
      options: available.countries.map((c) => ({ value: c, text: label(c) })),
    },
    {
      key: 'licenseTypes',
      title: 'License Type',
      options: available.licenseTypes.map((c) => ({
        value: c,
        text: label(c),
      })),
    },
    {
      key: 'routed',
      title: 'Routed',
      options: available.routed.map((v) => ({
        value: v,
        text: v === 'Y' ? 'Routed (Y)' : 'Not routed (N)',
      })),
    },
  ];

  return (
    <div
      className='audit-add-modal-cont'
      style={{ maxWidth: '1000px', width: '100%' }}
    >
      <div className='audit-add-modal-cont-head'>
        <div className='audit-add-modal-head-cont-change'>
          <div className='audit-add-modal-head-cont-icon'>
            <FilterListIcon
              sx={{ color: '#4895EF', width: '25px', height: '24px' }}
            />
          </div>
          <div className='audit-add-modal-head-cont-supporting'>
            <h1>Filter Results</h1>
            <p>
              Narrow the lines by severity, finding, destination, license type
              or routed status.
            </p>
          </div>
        </div>
        <div
          className='audit-add-modal-head-close'
          onClick={handleClose}
          style={{ cursor: 'pointer' }}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='24'
            height='24'
            viewBox='0 0 24 24'
            fill='none'
          >
            <path
              d='M18 6L6 18M6 6L18 18'
              stroke='#85888E'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>
      </div>

      <div className='activity-cont-frame-left-table-feed-row-cont-divider'></div>

      <div
        className='audit-add-modal-cont-body'
        style={{
          paddingTop: '24px',
          paddingBottom: '24px',
          maxHeight: '550px',
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: '32px',
            width: '100%',
            paddingLeft: '24px',
            paddingRight: '24px',
          }}
        >
          {/* No Routed column in the upload: the routed filter has nothing to offer. */}
          {columns
            .filter((col) => col.key !== 'routed' || col.options.length > 0)
            .map((col) => (
              <div key={col.key} style={{ flex: 1, minWidth: 0 }}>
                <h3 style={columnTitle}>{col.title}</h3>
                {col.options.length ? (
                  <div style={columnList}>
                    {col.options.map((opt) => (
                      <CheckRow
                        key={opt.value}
                        selected={local[col.key].includes(opt.value)}
                        onToggle={() => toggle(col.key, opt.value)}
                      >
                        {opt.text}
                      </CheckRow>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>
                    None on this run
                  </p>
                )}
              </div>
            ))}
        </div>
      </div>

      <div className='activity-cont-frame-left-table-feed-row-cont-divider'></div>

      <div className='audit-modal-cont-footer'>
        <div
          className='audit-modal-cont-footer-content'
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <div
            className='audit-modal-cont-footer-content-btn-cancel'
            onClick={() => setLocal(EMPTY_AES_FILTERS)}
            style={{ width: '150px', padding: '0 24px', flex: 'none' }}
          >
            <div className='audit-modal-cont-footer-content-btn-text'>
              Clear All
            </div>
          </div>
          <div
            className='audit-modal-cont-footer-content-btn-upload'
            onClick={() => onApplyFilters(local)}
            style={{ width: '150px', padding: '0 24px', flex: 'none' }}
          >
            <div
              className='audit-modal-cont-footer-content-btn-text'
              style={{
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                whiteSpace: 'nowrap',
              }}
            >
              {isLoading && (
                <CircularProgress
                  style={{ width: '16px', height: '16px', marginRight: '8px' }}
                  color='inherit'
                />
              )}
              Apply Filters
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AesFilterModal;
